import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { withTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Button, Grid, IconButton, useMediaQuery, Box } from "@mui/material";
import ViewIcon from "@mui/icons-material/RemoveRedEye";
import EditIcon from "@mui/icons-material/Edit";
import moment from "moment";
import { useFormik } from "formik";
import PropTypes from "prop-types";

import Backdrop from "Components/Common/Backdrop";
import BreadCrumb from "Components/Common/BreadCrumb";
import BaseTable from "Components/Common/BaseTable";
import DocumentPreviewModal from "Components/Common/DocumentPreviewModal";
import Tooltip from "Components/Common/Tooltip";
import {
  consumerStatusOptions,
  getOptionLabel,
  getOptionValue,
} from "common/enums";
import { SUPER_ADMIN, powerAdmin3Roles } from "common/constant";
import InputFieldText from "Components/Common/InputFieldText";
import SelectField from "Components/Common/SelectField";
import EditPowerStatusModal from "pages/Admin/UnpaidBills/EditPowerStatusModal";

import { getImageUrl, isAuthorizedUser } from "utils";
import { convertUTCDateToLocalDate } from "utils/convertUTCDateToLocalDate";

import { useLazyGetTemporaryDisconnectedConsumersQuery } from "apis/Billing";
import { useGetBillingCycleOptionsQuery } from "apis/BillingCycle";

const TemporaryDisconnectedConsumers = ({ t }) => {
  const [isResetPageRegistered, setIsResetPageRegistered] = useState(false);
  const [documentToPreview, setDocumentToPreview] = useState("");
  const [payloadValues, setPayloadValues] = useState(null);

  const { roles } = useSelector((state) => state.Login);

  const resetPageRef = useRef();

  const [
    getTemporaryDisconnectedConsumers,
    {
      data: { data: temporaryDisconnectedConsumers } = {},
      isLoading,
      isFetching,
    },
  ] = useLazyGetTemporaryDisconnectedConsumersQuery();

  const { data: { data: billingCycleOptions } = {} } =
    useGetBillingCycleOptionsQuery();

  const handleResetPage = useCallback(resetPageRef.current, [
    isResetPageRegistered,
  ]);

  const md = useMediaQuery("(min-width: 600px) and (max-width: 899px)");
  const sm = useMediaQuery("(max-width: 599px)");

  const updatedBillingCycleOptions = useMemo(
    () =>
      billingCycleOptions?.length
        ? billingCycleOptions
            .filter(
              ({ subscriptionType }) =>
                subscriptionType ===
                getOptionValue("subscriptionTypes", "Electricity")
            )
            .map(({ billDateDuration, id, status }) => ({
              label: billDateDuration,
              value: id,
              status,
            }))
        : [],
    [billingCycleOptions]
  );

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      billCycleId: "",
      consumerNumber: "",
      meterNumber: "",
      status: "",
    },
    onSubmit: async ({ billCycleId, consumerNumber, meterNumber, status }) => {
      try {
        await getTemporaryDisconnectedConsumers({
          billingCycleId: billCycleId,
          consumerNumber,
          meterNumber,
          status,
        });
        handleResetPage();
      } catch (error) {}
    },
  });

  const { handleSubmit, setFieldValue } = formik;

  useEffect(() => {
    (async () => {
      try {
        await getTemporaryDisconnectedConsumers();
      } catch (error) {}
    })();
  }, []);

  const columns = useMemo(
    () => [
      { title: "Bill Cycle", field: "billCycle" },
      { title: "Consumer Number", field: "consumerNumber" },
      { title: "Consumer Name", field: "consumerName" },
      { title: "Net Payable", field: "netPayable" },
      { title: "Pending Dues", field: "pendingDues" },
      {
        title: "Status",
        renderCell: ({ status }) =>
          getOptionLabel("billCalculationStatus", status),
      },
      {
        title: "Payment Due Date",
        renderCell: ({ paymentDueDate }) =>
          moment(paymentDueDate).isValid()
            ? convertUTCDateToLocalDate(paymentDueDate).format("DD-MM-YYYY")
            : "N/A",
      },
      {
        title: "Actions",
        renderCell: ({
          url,
          utilityConsumerId,
          billCalculations,
          paymentDueDate,
          netPayable,
          status,
        }) => (
          <Box minWidth="100px">
            <IconButton
              onClick={() =>
                setDocumentToPreview({
                  url: getImageUrl(url),
                  documentTypeName: "Bill",
                  documentType: "pdf",
                  documentName: "bill",
                })
              }
            >
              <Tooltip title={t("View Bill Receipt")}>
                <ViewIcon
                  fontSize="small"
                  color="primary"
                  className="cursor-pointer"
                />
              </Tooltip>
            </IconButton>
            {isAuthorizedUser(roles, [SUPER_ADMIN, ...powerAdmin3Roles]) &&
              getOptionValue("billCalculationStatus", "Paid") === status && (
                <IconButton
                  onClick={() =>
                    setPayloadValues({
                      utilityConsumerId,
                      billCalculationIds: billCalculations.map(({ id }) => id),
                      paymentDueDate,
                      netPayable,
                    })
                  }
                >
                  <Tooltip title={t("Edit Power status")}>
                    <EditIcon
                      fontSize="small"
                      color="primary"
                      className="cursor-pointer"
                    />
                  </Tooltip>
                </IconButton>
              )}
          </Box>
        ),
      },
    ],
    [temporaryDisconnectedConsumers]
  );

  const clearBtnHandler = async () => {
    try {
      setFieldValue("billCycleId", "");
      setFieldValue("consumerNumber", "");
      setFieldValue("meterNumber", "");
      setFieldValue("status", "");
      await getTemporaryDisconnectedConsumers();
      handleResetPage();
    } catch (error) {}
  };

  return (
    <>
      <Backdrop open={isLoading || isFetching} />
      {!!documentToPreview && (
        <DocumentPreviewModal
          isOpen
          handleClose={() => setDocumentToPreview(null)}
          {...documentToPreview}
        />
      )}

      {!!payloadValues && (
        <EditPowerStatusModal
          payloadValues={payloadValues}
          handleClose={() => setPayloadValues(null)}
          isTemporaryDisconnected
        />
      )}
      <Backdrop open={isLoading} />
      <BreadCrumb title={t("Temporary Disconnected Consumers")} />
      <form onSubmit={handleSubmit}>
        <Grid container columnSpacing={2} className="mb-1">
          <Grid item lg={3} md={4} sm={6} xs={12}>
            <SelectField
              label={t("Select bill cycle")}
              name="billCycleId"
              formik={formik}
              options={updatedBillingCycleOptions || []}
            />
          </Grid>
          <Grid item lg={3} md={4} sm={6} xs={12}>
            <InputFieldText
              label={t("Consumer Number")}
              name="consumerNumber"
              formik={formik}
              placeholder={t("Enter consumer number")}
            />
          </Grid>
          <Grid item lg={3} md={4} sm={6} xs={12}>
            <InputFieldText
              label={t("Meter Number")}
              name="meterNumber"
              formik={formik}
              placeholder={t("Enter meter number")}
            />
          </Grid>
          <Grid item lg={3} md={4} sm={6} xs={12}>
            <SelectField
              label={t("Select Status")}
              name="status"
              formik={formik}
              options={consumerStatusOptions}
            />
          </Grid>
          <Grid item>
            <Button variant="outlined" className="mb-2 me-2" type="submit">
              {t("Search")}
            </Button>
            <Button
              variant="outlined"
              onClick={clearBtnHandler}
              className="mb-2 me-2"
            >
              {t("Clear")}
            </Button>
          </Grid>
        </Grid>
      </form>
      <BaseTable
        columns={columns}
        data={temporaryDisconnectedConsumers}
        pageSize={10}
        registerResetPage={(cb) => {
          resetPageRef.current = cb;
          setIsResetPageRegistered((prev) => !prev);
        }}
        maxHeight={
          sm
            ? "calc(100vh - 350px)"
            : md
            ? "calc(100vh - 300px)"
            : "calc(100vh - 250px)"
        }
      />
    </>
  );
};

TemporaryDisconnectedConsumers.propTypes = {
  t: PropTypes.func,
};

export default withTranslation()(TemporaryDisconnectedConsumers);
